/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, September 27th 2022, 11:58:03 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { DEPENDENCY_ACTIONS, FieldDependency, FieldDependencyAction, FormField } from "lib/schemas";
import { getDefaultFieldDependencyAction, getDefaultPermittedValue } from "lib/factories";
import { FieldPicker } from "form-builder/components";
import { TaskActionsIcon, FormIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";
import { useOSAPIFormContext } from "ui-widgets/forms/providers";
import { OSDropDown, OSLoadingButton, StyledOSDialog, OSDeleteButton, OSAlert, ValueSelector } from "ui-widgets";
import { PermittedValuesChipButton } from "./PermittedValuesChipButton";

type FieldDependencyFormActionsProps = { fieldDependency: FieldDependency };

export const FieldDependencyFormActions = (props: FieldDependencyFormActionsProps) => {
  const { errors, fieldDependencyActions, addNewDependencyAction, onChangeDependencyAction, removeDependencyAction } =
    useFieldDependencyFormActions(props);

  return (
    <>
      <Stack direction="row" spacing={2} className="title">
        <TaskActionsIcon width={30} height={30} />
        <Typography variant="h5">Actions</Typography>
      </Stack>
      <Stack spacing={2}>
        {errors?.length ? (
          <OSAlert severity="error" variant="filled">
            {errors.map((error, index) => (
              <React.Fragment key={`${index}`}>
                {Object.keys(error).map((error_key) => (
                  <p key={error_key}>{error?.[error_key]["message"]}</p>
                ))}
              </React.Fragment>
            ))}
          </OSAlert>
        ) : null}
        {fieldDependencyActions.map((fieldDependencyAction) => {
          return (
            <React.Fragment key={fieldDependencyAction?.artifact_guid}>
              <FieldDependencyFormAction
                fieldDependencyAction={fieldDependencyAction}
                onChangeDependencyAction={onChangeDependencyAction}
                handleDependencyAction={() => removeDependencyAction(fieldDependencyAction?.artifact_guid)}
              />
              <Divider />
            </React.Fragment>
          );
        })}
        <Tooltip title={<Typography>Add New Action</Typography>} arrow placement="top">
          <div className="add-button">
            <OSLoadingButton
              isLoading={false}
              actionStyle="add"
              buttonType="floating"
              onClick={addNewDependencyAction}
              sx={{ mt: 1 }}
            />
          </div>
        </Tooltip>
      </Stack>
    </>
  );
};

type OnChangeDependencyAction = (
  fieldDependencyActionGUID: FieldDependencyAction["artifact_guid"],
  data: FieldDependencyAction
) => void;

const useFieldDependencyFormActions = ({ fieldDependency }: FieldDependencyFormActionsProps) => {
  const {
    useFormReturn: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useOSAPIFormContext<FieldDependency>();
  const fieldDependencyActions = watch("field_dependency_actions");

  const onChangeDependencyAction: OnChangeDependencyAction = (
    fieldDependencyActionGUID: FieldDependencyAction["artifact_guid"],
    data: FieldDependencyAction
  ) => {
    const newFieldDependencyActions = fieldDependencyActions.map((action) => {
      if (action.artifact_guid === fieldDependencyActionGUID) {
        return data;
      }
      return action;
    });
    setValue("field_dependency_actions", newFieldDependencyActions, { shouldDirty: true });
  };

  const addNewDependencyAction = () => {
    setValue(
      "field_dependency_actions",
      [...fieldDependencyActions, getDefaultFieldDependencyAction({ fieldDependency })],
      { shouldDirty: true }
    );
  };

  const removeDependencyAction = (fieldDependencyActionGUID: FieldDependencyAction["artifact_guid"]) => {
    setValue(
      "field_dependency_actions",
      fieldDependencyActions.filter(
        (fieldDependencyAction) => fieldDependencyAction.artifact_guid !== fieldDependencyActionGUID
      ),
      { shouldDirty: true }
    );
  };

  return {
    errors: errors?.["field_dependency_actions"],
    fieldDependencyActions,
    addNewDependencyAction,
    onChangeDependencyAction,
    removeDependencyAction,
  };
};

type FieldDependencyFormActionProps = {
  fieldDependencyAction: FieldDependencyAction;
  onChangeDependencyAction: OnChangeDependencyAction;
  handleDependencyAction: () => void;
};

const FieldDependencyFormAction = (props: FieldDependencyFormActionProps) => {
  const {
    formId,
    selectedDependentField,
    setSelectedDependentField,
    onAddPermittedValue,
    handleDeletePermittedValue,
    onChangeDependentField,
    onChangeActionType,
  } = useFieldDependencyFormAction(props);

  const { fieldDependencyAction, onChangeDependencyAction, handleDependencyAction } = props;
  return (
    <Stack direction="row" spacing={2}>
      <Stack sx={{ flexBasis: "50%" }}>
        <OSDropDown
          value={fieldDependencyAction.dependent_field_action}
          options={DEPENDENCY_ACTIONS}
          label="Action"
          onChangeCallBack={onChangeActionType}
        />
      </Stack>

      <div style={{ flexBasis: "50%" }}>
        <FieldPicker
          onLoadIntial={setSelectedDependentField}
          handleChooseField={onChangeDependentField}
          fieldIdentifier={fieldDependencyAction.dependent_field}
          label="Choose Dependent Field"
          formId={formId}
          apiQueryParams={{ include_choices: true }}
        />
      </div>
      {fieldDependencyAction.dependent_field_action === "permit" && selectedDependentField && (
        <StyledOSDialog
          open={null}
          popUpTitle="Permitted Values"
          popUpIcon={<FormIcon fill={COLORSCHEME.secondary} />}
          renderOpenButton={({ handleOpen }) => (
            <Tooltip title={<Typography>Edit Permitted Values</Typography>} arrow placement="top">
              <div className="permitted-values-button">
                <PermittedValuesChipButton
                  action_permitted_values={fieldDependencyAction.action_permitted_values}
                  onClick={handleOpen}
                />
              </div>
            </Tooltip>
          )}
        >
          <Stack spacing={2} className="permitted-values">
            {fieldDependencyAction.action_permitted_values?.map(
              (action_permitted_value, action_permitted_value_index) => (
                <Stack key={action_permitted_value.id} direction="row">
                  <ValueSelector
                    label="Permitted Value"
                    chosenValue={action_permitted_value.value}
                    onChangeValue={(fieldValue) =>
                      onChangeDependencyAction(fieldDependencyAction?.artifact_guid, {
                        ...fieldDependencyAction,
                        action_permitted_values: [
                          ...fieldDependencyAction.action_permitted_values?.filter(
                            (act) => act.id !== action_permitted_value.id
                          ),
                          { ...action_permitted_value, value: fieldValue },
                        ],
                      })
                    }
                    choices={selectedDependentField?.resolved_choices}
                  />
                  <OSDeleteButton
                    onClick={() => handleDeletePermittedValue(action_permitted_value?.id, action_permitted_value_index)}
                  />
                </Stack>
              )
            )}
            <Tooltip title={<Typography>Add Permitted Value</Typography>} arrow placement="top">
              <div style={{ marginLeft: "auto" }}>
                <OSLoadingButton
                  isLoading={false}
                  actionStyle="add"
                  buttonType="floating"
                  onClick={onAddPermittedValue}
                  sx={{ mt: 1 }}
                />
              </div>
            </Tooltip>
          </Stack>
        </StyledOSDialog>
      )}
      {fieldDependencyAction.dependent_field_action === "default_value" && selectedDependentField && (
        <TextField
          value={fieldDependencyAction.default_value}
          onChange={(e) =>
            onChangeDependencyAction(fieldDependencyAction?.artifact_guid, {
              ...fieldDependencyAction,
              default_value: e.target.value,
            })
          }
        />
      )}
      <OSDeleteButton onClick={handleDependencyAction} />
    </Stack>
  );
};

const useFieldDependencyFormAction = ({
  fieldDependencyAction,
  onChangeDependencyAction,
}: FieldDependencyFormActionProps) => {
  const {
    useFormReturn: { watch },
  } = useOSAPIFormContext<FieldDependency>();
  const formId = watch("form");
  const [selectedDependentField, setSelectedDependentField] = React.useState<FormField>();

  return {
    formId,
    selectedDependentField,
    setSelectedDependentField,
    onAddPermittedValue: () =>
      onChangeDependencyAction(fieldDependencyAction?.artifact_guid, {
        ...fieldDependencyAction,
        action_permitted_values: [...fieldDependencyAction.action_permitted_values, getDefaultPermittedValue()],
      }),
    handleDeletePermittedValue: (valueId, valueIndex) => {
      onChangeDependencyAction(fieldDependencyAction?.artifact_guid, {
        ...fieldDependencyAction,
        action_permitted_values: fieldDependencyAction.action_permitted_values?.filter((permitted_value, idx) => {
          if (Boolean(valueId)) return permitted_value.id !== valueId;
          return idx !== valueIndex; // Catering for legacy configurations that might not have an id.
        }),
      });
    },
    onChangeDependentField: (field) => {
      setSelectedDependentField(field);
      onChangeDependencyAction(fieldDependencyAction?.artifact_guid, {
        ...fieldDependencyAction,
        dependent_field: field?.id,
      });
    },
    onChangeActionType: (action) =>
      onChangeDependencyAction(fieldDependencyAction?.artifact_guid, {
        ...fieldDependencyAction,
        dependent_field_action: action,
      }),
  };
};
